import { useState } from "react";
import ConsoleLayout from "@/components/ConsoleLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { User, Shield, Palette, Save, ChevronRight, Sun, Moon, Eye, EyeOff, Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { usersApi, authApi } from "@/lib/api";
import { toast } from "sonner";

function ProfileTab() {
  const { t } = useLanguage();
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState(user?.display_name || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await usersApi.updateMe({ display_name: name });
      await refreshUser();
      toast.success("Profile updated");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-4 p-5 sci-card">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/30 to-cyan-500/30 border border-blue-500/20 flex items-center justify-center text-2xl font-bold text-blue-400">
          {user?.display_name?.charAt(0).toUpperCase() || "U"}
        </div>
        <div>
          <div className="text-sm font-medium text-white mb-0.5">{user?.display_name}</div>
          <div className="text-xs text-slate-500">{user?.email}</div>
        </div>
      </div>

      <div className="sci-card p-5 space-y-4">
        <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t("settings.personalInfo")}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">{t("settings.fullName")}</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-white/5 border-white/10 text-white text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">{t("settings.email")}</label>
            <Input
              value={user?.email || ""}
              disabled
              className="bg-white/5 border-white/10 text-slate-500 text-sm opacity-50"
            />
          </div>
        </div>
        <Button size="sm" className="btn-primary-sci gap-1.5 text-xs h-8" onClick={handleSave} disabled={saving}>
          <Save size={12} />
          {saving ? "Saving..." : t("settings.save")}
        </Button>
      </div>
    </div>
  );
}

function SettingsPasswordInput({
  id,
  value,
  onChange,
  placeholder,
  autoComplete,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        id={id}
        type={show ? "text" : "password"}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        className="bg-white/5 border-white/10 text-white text-sm pr-10"
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
      >
        {show ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
  );
}

function isPasswordValid(password: string): boolean {
  return (
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /[0-9]/.test(password)
  );
}

function SecurityTab() {
  const { t } = useLanguage();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const passwordValid = isPasswordValid(newPassword);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
  const canSubmit = oldPassword.length > 0 && passwordValid && passwordsMatch && newPassword !== oldPassword;

  const rules = [
    { key: "min8", label: t("changePwd.rule.min8"), test: newPassword.length >= 8 },
    { key: "upper", label: t("changePwd.rule.upper"), test: /[A-Z]/.test(newPassword) },
    { key: "lower", label: t("changePwd.rule.lower"), test: /[a-z]/.test(newPassword) },
    { key: "digit", label: t("changePwd.rule.digit"), test: /[0-9]/.test(newPassword) },
  ];

  const submit = async () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.error(t("settings.pwdAllRequired"));
      return;
    }
    if (!passwordValid) {
      toast.error(t("settings.pwdTooShort"));
      return;
    }
    if (newPassword === oldPassword) {
      toast.error(t("settings.pwdSameAsOld"));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t("settings.pwdMismatch"));
      return;
    }
    setSubmitting(true);
    try {
      await authApi.changePassword(oldPassword, newPassword);
      toast.success(t("settings.pwdUpdated"));
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || err?.message || "Failed to change password"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="sci-card p-5">
        <h3 className="text-sm font-semibold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t("settings.password")}</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">{t("settings.currentPassword")}</label>
            <SettingsPasswordInput
              id="settings-cp-old"
              value={oldPassword}
              onChange={setOldPassword}
              placeholder="••••••••"
              autoComplete="current-password"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">{t("settings.newPassword")}</label>
            <SettingsPasswordInput
              id="settings-cp-new"
              value={newPassword}
              onChange={setNewPassword}
              placeholder={t("changePwd.placeholder")}
              autoComplete="new-password"
            />
            {newPassword.length > 0 && (
              <div className="space-y-1 mt-2">
                {rules.map((r) => (
                  <div key={r.key} className="flex items-center gap-1.5 text-xs">
                    {r.test
                      ? <Check size={12} className="text-emerald-400" />
                      : <X size={12} className="text-slate-600" />}
                    <span className={r.test ? "text-emerald-400" : "text-slate-500"}>{r.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">{t("settings.confirmPassword")}</label>
            <SettingsPasswordInput
              id="settings-cp-confirm"
              value={confirmPassword}
              onChange={setConfirmPassword}
              placeholder="••••••••"
              autoComplete="new-password"
            />
            {confirmPassword.length > 0 && (
              <div className="flex items-center gap-1.5 text-xs mt-1">
                {passwordsMatch
                  ? <><Check size={12} className="text-emerald-400" /><span className="text-emerald-400">{t("changePwd.match")}</span></>
                  : <><X size={12} className="text-red-400" /><span className="text-red-400">{t("changePwd.noMatch")}</span></>}
              </div>
            )}
          </div>
          <Button
            size="sm"
            className="btn-primary-sci gap-1.5 text-xs h-8"
            onClick={submit}
            disabled={submitting || !canSubmit}
          >
            {submitting ? "..." : t("settings.updatePassword")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PreferencesTab() {
  const { t, language, setLanguage } = useLanguage();
  const { platformTheme, setPlatformTheme } = useTheme();
  const [lang, setLang] = useState(language === "en" ? "en-US" : "zh-CN");

  const handleLangChange = (value: string) => {
    setLang(value);
    setLanguage(value === "en-US" ? "en" : "zh");
  };

  return (
    <div className="space-y-5">
      {/* Platform Theme */}
      <div className="sci-card p-5">
        <h3 className="text-sm font-semibold text-foreground mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t("settings.platformTheme")}</h3>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setPlatformTheme("tech")}
            className={cn(
              "p-4 rounded-xl border-2 text-left transition-all",
              platformTheme === "tech"
                ? "border-blue-500 bg-blue-500/10"
                : "border-border hover:border-blue-500/40"
            )}
          >
            <div className="w-10 h-10 rounded-xl bg-[#0A0F1E] border border-white/10 flex items-center justify-center mb-3">
              <Moon size={16} className="text-blue-400" />
            </div>
            <div className="text-sm font-medium text-foreground">{t("settings.themeTech")}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{t("settings.themeTechDesc")}</div>
          </button>
          <button
            onClick={() => setPlatformTheme("fresh")}
            className={cn(
              "p-4 rounded-xl border-2 text-left transition-all",
              platformTheme === "fresh"
                ? "border-blue-500 bg-blue-500/10"
                : "border-border hover:border-blue-500/40"
            )}
          >
            <div className="w-10 h-10 rounded-xl bg-[#F8FAFB] border border-slate-200 flex items-center justify-center mb-3">
              <Sun size={16} className="text-amber-500" />
            </div>
            <div className="text-sm font-medium text-foreground">{t("settings.themeFresh")}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{t("settings.themeFreshDesc")}</div>
          </button>
        </div>
      </div>

      {/* Other Preferences */}
      <div className="sci-card p-5 space-y-5">
        <h3 className="text-sm font-semibold text-foreground" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t("settings.preferences")}</h3>
        {[
          { label: t("settings.language"), value: lang, options: [["zh-CN", "中文（简体）"], ["en-US", "English"]], setter: handleLangChange },
        ].map(({ label, value, options, setter }) => (
          <div key={label}>
            <label className="text-xs text-muted-foreground mb-1.5 block">{label}</label>
            <select
              value={value}
              onChange={(e) => setter(e.target.value)}
              className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-blue-500/50"
            >
              {options.map(([val, label]) => (
                <option key={val} value={val} className="bg-card">{label}</option>
              ))}
            </select>
          </div>
        ))}
        <Button size="sm" className="btn-primary-sci gap-1.5 text-xs h-8">
          <Save size={12} />
          {t("settings.savePrefs")}
        </Button>
      </div>
    </div>
  );
}

export default function Settings() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("profile");

  const TABS = [
    { id: "profile", label: t("settings.profile"), icon: <User size={14} /> },
    { id: "security", label: t("settings.security"), icon: <Shield size={14} /> },
    { id: "preferences", label: t("settings.preferences"), icon: <Palette size={14} /> },
  ];

  return (
    <ConsoleLayout title={t("settings.title")} subtitle={t("settings.subtitle")}>
      <div className="p-6">
        <div className="flex gap-6">
          {/* Sidebar */}
          <div className="w-[180px] flex-shrink-0 space-y-1">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition-all text-left",
                  activeTab === tab.id
                    ? "bg-blue-500/15 text-blue-400 border border-blue-500/20"
                    : "text-slate-500 hover:text-slate-300 hover:bg-white/5"
                )}
              >
                {tab.icon}
                {tab.label}
                {activeTab === tab.id && <ChevronRight size={12} className="ml-auto" />}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {activeTab === "profile" && <ProfileTab />}
            {activeTab === "security" && <SecurityTab />}
            {activeTab === "preferences" && <PreferencesTab />}
          </div>
        </div>
      </div>
    </ConsoleLayout>
  );
}
